// The inbox connection: one socket per signed-in tab (`/v1/realtime/inbox/{userId}`). The server sends a
// `signal` of kind `inbox` into it after a commit that notified this person; the client then reads the
// unread count again. It also asks once on every welcome, so a notification that arrived while the
// socket was down is not missed. Fetches a fresh ticket for every connect, pings every 25 s and
// reconnects with backoff, like the presence client. When the server has no realtime (503, or an older
// server without the route) it says "unavailable" and the inbox keeps polling as before. Pure: socket,
// timers and the ticket call are injected.
import { type SocketLike, socketUrl } from "../canvas/connection.ts";
import { backoffMs, shouldReconnect } from "../canvas/presence.ts";
import {
  isUnavailable,
  PRESENCE_PING_MS,
  type PresenceTimers,
  UNAVAILABLE_RETRY_MS,
} from "./presenceClient.ts";

/** Close code the room sends when a socket outlived its ticket: reconnect at once. */
const EXPIRED = 4408;
const OPEN = 1;

/** `live`: signals arrive, so the inbox may poll slowly. Anything else: it polls as it did without push. */
export type InboxPushStatus = "idle" | "connecting" | "live" | "offline" | "unavailable";

export interface InboxClientOptions {
  getSession: () => Promise<{ ticket: string; path: string }>;
  createSocket: (url: string) => SocketLike;
  apiBase: string;
  pageOrigin: string;
  clientId: string;
  timers: PresenceTimers;
  rand?: () => number;
  /** The inbox changed (or may have, after a reconnect): read the unread count again. */
  onSignal: () => void;
  onStatus: (s: InboxPushStatus) => void;
}

export class InboxClient {
  private readonly o: InboxClientOptions;
  private ws: SocketLike | null = null;
  private stopped = true;
  private epoch = 0;
  private attempt = 0;
  private retry: unknown = null;
  private ping: unknown = null;
  private st: InboxPushStatus = "idle";

  constructor(options: InboxClientOptions) {
    this.o = options;
  }

  get status(): InboxPushStatus {
    return this.st;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.epoch++;
    if (this.retry !== null) this.o.timers.clearTimeout(this.retry);
    this.retry = null;
    this.clearPing();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      ws.onmessage = null;
      try {
        ws.close(1000, "bye");
      } catch {
        // already closed
      }
    }
    this.set("idle");
  }

  private set(s: InboxPushStatus): void {
    if (s === this.st) return;
    this.st = s;
    this.o.onStatus(s);
  }

  private async connect(): Promise<void> {
    const epoch = ++this.epoch;
    if (this.st !== "live") this.set("connecting");
    let session: { ticket: string; path: string };
    try {
      session = await this.o.getSession();
    } catch (e) {
      if (epoch !== this.epoch || this.stopped) return;
      if (isUnavailable(e)) {
        this.set("unavailable");
        return this.scheduleRetry(UNAVAILABLE_RETRY_MS);
      }
      this.set("offline");
      return this.scheduleRetry();
    }
    if (epoch !== this.epoch || this.stopped) return;
    let ws: SocketLike;
    try {
      ws = this.o.createSocket(socketUrl(this.o.apiBase, session.path, session.ticket, this.o.pageOrigin));
    } catch {
      this.set("offline");
      return this.scheduleRetry();
    }
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      ws.send(JSON.stringify({ t: "hello", clientId: this.o.clientId }));
      this.clearPing();
      this.ping = this.o.timers.setInterval(() => {
        if (this.ws?.readyState === OPEN) this.ws.send(JSON.stringify({ t: "ping" }));
      }, PRESENCE_PING_MS);
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws || typeof ev.data !== "string") return;
      let msg: { t?: unknown; kind?: unknown };
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (!msg || typeof msg !== "object") return;
      if (msg.t === "welcome") {
        this.attempt = 0;
        this.set("live");
        this.o.onSignal();
      } else if (msg.t === "signal" && msg.kind === "inbox") this.o.onSignal();
    };
    ws.onerror = () => {
      // the close event follows
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.clearPing();
      if (this.stopped) return;
      this.set("offline");
      if (ev.code === EXPIRED) {
        this.attempt = 0;
        void this.connect();
      } else if (shouldReconnect(ev.code)) this.scheduleRetry();
    };
  }

  private scheduleRetry(ms?: number): void {
    if (this.stopped || this.retry !== null) return;
    const wait = ms ?? backoffMs(this.attempt++, (this.o.rand ?? Math.random)());
    this.retry = this.o.timers.setTimeout(() => {
      this.retry = null;
      if (!this.stopped) void this.connect();
    }, wait);
  }

  private clearPing(): void {
    if (this.ping !== null) this.o.timers.clearInterval(this.ping);
    this.ping = null;
  }
}
